import { ServiceType } from '../../../types/core';
import { Language, TranslationRecord } from '../../../types/language';
import { PublicIslandJourney } from '../types';

export type JourneyGroup = 'passengers' | 'vehicles' | 'cargo';

export const groupJourneysByTab = (
  journeys: PublicIslandJourney[],
): Record<JourneyGroup, PublicIslandJourney[]> =>
  journeys.reduce(
    (acc, journey) => {
      switch (journey.serviceType) {
        case ServiceType.FerryPassenger:
        case ServiceType.FerryPassengerAndLightCargo:
        case ServiceType.FastFerry:
          acc.passengers.push(journey);
          break;
        case ServiceType.FerryRollOnRollOff:
          acc.vehicles.push(journey);
          break;
        case ServiceType.FerryHeavyCargo:
          acc.cargo.push(journey);
          break;
        default:
          break;
      }
      return acc;
    },
    {
      passengers: [],
      vehicles: [],
      cargo: [],
    } as Record<JourneyGroup, PublicIslandJourney[]>,
  );


export function formatJourneyPortLabel(
  journey: PublicIslandJourney,
  language: Language,
  currentIslandId: string,
  direction: 'outbound' | 'inbound',
): string {
  const islandId =
    direction === 'outbound'
      ? journey.departureIslandId
      : journey.arrivalIslandId;

  const islandName =
    direction === 'outbound'
      ? journey.departureIslandNames[language]
      : journey.arrivalIslandNames[language];

  const portName =
    direction === 'outbound'
      ? journey.departurePortNames[language]
      : journey.arrivalPortNames[language];

  // Rule 1: island matches URL param → show port only
  if (islandId === currentIslandId) {
    return portName;
  }

  // Rule 2: island and port names are equal → show port only
  if (islandName === portName) {
    return portName;
  }

  // Default: IslandName - PortName
  return `${islandName} - ${portName}`;
}

export function getJourneyChanges(
  journey: PublicIslandJourney,
  language: keyof TranslationRecord,
) {
  const timeMismatch =
    journey.routeDepartureTime !== null &&
    journey.routeDepartureTime !== journey.time;

  const departureIslandMismatch =
    journey.routeDepartureIslandId &&
    journey.routeDepartureIslandId !== journey.departureIslandId;

  const departurePortMismatch =
    journey.routeDeparturePortNames &&
    journey.routeDeparturePortNames[language] !==
      journey.departurePortNames[language];

  const arrivalIslandMismatch =
    journey.routeArrivalIslandId &&
    journey.routeArrivalIslandId !== journey.arrivalIslandId;

  const arrivalPortMismatch =
    journey.routeArrivalPortNames &&
    journey.routeArrivalPortNames[language] !==
      journey.arrivalPortNames[language];

  const departureMismatch = departureIslandMismatch || departurePortMismatch;
  const arrivalMismatch = arrivalIslandMismatch || arrivalPortMismatch;

  return {
    timeMismatch,
    departureMismatch,
    arrivalMismatch,
  };
}
